import React, { useEffect, useState, useMemo } from "react";
import AddSubRoleModal from "./AddSubRoleModal";
import ViewSubRoleModal from "./ViewSubRoleModal";
import "../../../css/RolesPage.css"; // Imports the refactored CSS
import { Role as BaseRole } from '../../../types/index';
import api from '../../../api/axiosConfig';

// Create an extended type for our state to hold the parent role's name
interface EnrichedSubRole extends BaseRole {
  parentName?: string;
}

const SubRoleTable: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSubRole, setSelectedSubRole] = useState<EnrichedSubRole | null>(null);
  // Update state to use the new enriched type
  const [subRoles, setSubRoles] = useState<EnrichedSubRole[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modify fetchSubRoles to get parent role names
  const fetchSubRoles = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found");

      // First, get the list of all roles to identify the sub-roles
      const response = await api.auth_api.get<BaseRole[]>("/roles", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const initialSubRoles = response.data.filter((role) => role.parent !== null);

      // Then, create promises to fetch detailed info for each sub-role concurrently
      const detailPromises = initialSubRoles.map(subRole =>
        api.auth_api.get(`/roles/subroles/${subRole._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );

      // Wait for all detail fetches to complete
      const detailResponses = await Promise.all(detailPromises);

      // Combine the original data with the fetched parent name
      const enrichedSubRoles = initialSubRoles.map((subRole, index) => {
        const parentInfo = detailResponses[index].data.parent;
        return {
          ...subRole,
          parentName: parentInfo ? parentInfo.name : "N/A", // Add the parent name
        };
      });

      setSubRoles(enrichedSubRoles);

    } catch (error) {
      console.error("Error fetching sub-roles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubRoles();
  }, []);

  const filteredSubRoles = useMemo(() => {
    return subRoles
      .filter(role => {
        if (!searchTerm) return true;
        return role.name.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .filter(role => {
        if (statusFilter === 'All') return true;
        const isActive = statusFilter === 'Active';
        return role.isActive === isActive;
      })
      .filter(role => {
        if (typeFilter === 'All') return true;
        // Handle potential whitespace differences
        return role.type.trim().toLowerCase() === typeFilter.trim().toLowerCase();
      });
  }, [subRoles, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter]);

  const totalPages = Math.ceil(filteredSubRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubRolesToDisplay = filteredSubRoles.slice(startIndex, endIndex);

  const handleUpdateSubRole = (updatedRole: BaseRole) => {
    // When a role is updated, we should re-fetch to ensure parent info is fresh
    fetchSubRoles();
  };

  return (
    <div className="roles-page-table-container"> {/* Renamed */}
      <div className="roles-page-table-header"> {/* Renamed */}
        <div className="global-filters-container">
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by Sub role name..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="global-select-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select className="global-select-input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="All">All Types</option>
            {/* Make sure these values match the data */}
            <option value="Qavach Staff">QAVACH STAFF</option> 
            <option value="Tele Specialist">TELE SPECIALIST</option>
          </select>
        </div>
        <button className="roles-page-add-btn" onClick={() => setIsModalOpen(true)}> {/* Renamed */}
          + Add New Sub Role
        </button>
      </div>

      {loading ? (
        <p>Loading sub-roles...</p>
      ) : (
        <table className="roles-page-table"> {/* Renamed */}
          <thead>
            <tr>
              <th>Sub-Role Name</th>
              <th>Category</th>
              <th>Role</th>
              <th>Created On</th>
              <th>Disabled On</th>
              <th>Status</th>
              <th>Forms Assigned</th>
            </tr>
          </thead>
          <tbody>
            {currentSubRolesToDisplay.length > 0 ? (
              currentSubRolesToDisplay.map((sr) => (
                <tr
                  key={sr._id}
                  onClick={() => {
                    setSelectedSubRole(sr);
                    setIsViewModalOpen(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td>{sr.name}</td>
                  <td>
                    {/* Renamed classes */}
                    <span className={`roles-page-badge ${sr.type.toLowerCase().includes('qavach') ? "roles-page-badge-qavach" : "roles-page-badge-tele"}`}>
                      {sr.type}
                    </span>
                  </td>
                  <td>{sr.parentName}</td>
                  <td>{new Date(sr.createdAt).toLocaleDateString()}</td>
                  <td>{sr.disabledOn ? new Date(sr.disabledOn).toLocaleDateString() : "-"}</td>
                  <td>
                    {/* Renamed classes */}
                    <span className={`roles-page-status ${sr.isActive ? "roles-page-status-active" : "roles-page-status-inactive"}`}>
                      {sr.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{sr.formIds.length} Forms</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  No sub-roles match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Add Modal */}
      {isModalOpen && (
        <AddSubRoleModal
          onClose={() => setIsModalOpen(false)}
          parentRoles={[]} // Note: This might need to fetch parent roles
          onSubRoleAdded={fetchSubRoles}
        />
      )}

      {/* View/Edit Modal */}
      {isViewModalOpen && selectedSubRole && (
        <ViewSubRoleModal
          subRoleId={selectedSubRole._id}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedSubRole(null);
          }}
          onUpdate={handleUpdateSubRole}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="roles-page-pagination"> {/* Renamed */}
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(pageNumber => (
            <button
              key={pageNumber}
              onClick={() => setCurrentPage(pageNumber)}
              className={currentPage === pageNumber ? "active" : ""}
            >
              {pageNumber}
            </button>
          ))}
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SubRoleTable;